import { useQuery } from '@apollo/client';
import { type Location, useLocation } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { GetAudienceDocument } from '../../../graphql/query/audiences/GetAudience.generated.tsx';
import type { AudienceLocationState } from '../AudienceForm/AudienceFormFields.tsx';
import { useCreateAndActivate } from '../AudienceForm/hooks/useCreateAndActivate.ts';
import { getInitialEditValues } from '../AudienceForm/utils/getInitialEditValues.ts';
import { getInitialValues } from '../AudienceForm/utils/getInitialValues.ts';

export const useCreateAudience = () => {
  const { session } = useAuth();
  const location: Location<AudienceLocationState> = useLocation();
  const formValues = getInitialValues(location.state?.accountId ?? (session?.account.id as string));
  const shouldUseTemplate = location.state?.useAsTemplate === true && !!location.state?.audienceId;
  const { data: templateAudience, loading } = useQuery(GetAudienceDocument, {
    variables: { id: location.state?.audienceId || '' },
    skip: !shouldUseTemplate,
  });
  const createAndActivateAudience = useCreateAndActivate(session?.id as string);

  if (loading) {
    return {
      isLoading: true,
      createAndActivateAudience,
      values: formValues,
    };
  }

  const template = templateAudience?.audience_by_pk;

  if (!shouldUseTemplate || !template) {
    return {
      isLoading: false,
      createAndActivateAudience,
      values: formValues,
    };
  }

  const templateFormValues = getInitialEditValues(template);
  const templateDescription = template.description?.trim();

  return {
    isLoading: false,
    createAndActivateAudience,
    values: {
      ...formValues,
      ...templateFormValues,
      name: `Copy of ${template.name}`,
      description: templateDescription ? `Copy of ${templateDescription}` : '',
      account_id: formValues.account_id,
      audience_share: formValues.audience_share,
    },
  };
};
